import { cn } from '@/utils/cn';
import { Slot } from '@radix-ui/react-slot';
import { cva,type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const buttonVariants = cva(
  'inline-flex max-w-full min-w-touch items-center justify-center whitespace-normal break-words text-center text-sm font-semibold uppercase leading-tight ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40 min-h-touch rounded-sm active:translate-y-0 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:brightness-110 shadow-industrial active:shadow-industrial-active hover:translate-y-[-1px]',
        destructive: 'bg-destructive text-destructive-foreground hover:brightness-110 shadow-industrial active:shadow-industrial-active hover:translate-y-[-1px]',
        outline: 'border border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground hover:border-primary/30',
        secondary: 'bg-secondary text-secondary-foreground hover:brightness-110 shadow-industrial active:shadow-industrial-active hover:translate-y-[-1px]',
        ghost: 'hover:bg-accent hover:text-accent-foreground shadow-none',
        link: 'text-primary underline-offset-4 hover:underline shadow-none hover:translate-y-0',
        success: 'text-black shadow-industrial active:shadow-industrial-active hover:translate-y-[-1px]',
        warning: 'text-black shadow-industrial active:shadow-industrial-active hover:translate-y-[-1px]',
        batchOverride: 'bg-batch-override text-white hover:brightness-110 shadow-industrial active:shadow-industrial-active hover:translate-y-[-1px]',
      },
      size: {
        default: 'min-h-touch px-5 py-3',
        sm: 'min-h-touch px-3.5 py-2 text-sm',
        lg: 'min-h-14 px-7 py-3 text-base',
        xl: 'min-h-16 px-10 py-4 text-lg',
        icon: 'h-12 w-12 p-0',
        scanner: 'h-16 w-16 min-h-scanner min-w-scanner text-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, disabled, style, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    const extraStyle: React.CSSProperties = {};
    if (variant === 'default' || variant === undefined) {
      extraStyle.background = 'linear-gradient(180deg, #14B8A6 0%, #0F766E 100%)';
      extraStyle.letterSpacing = '0.06em';
      extraStyle.fontFamily = "'Outfit', sans-serif";
    } else if (variant === 'success') {
      extraStyle.background = 'linear-gradient(180deg, #4ADE80 0%, #22C55E 100%)';
      extraStyle.letterSpacing = '0.06em';
      extraStyle.fontFamily = "'Outfit', sans-serif";
    } else if (variant === 'warning') {
      extraStyle.background = 'linear-gradient(180deg, #FDE047 0%, #EAB308 100%)';
      extraStyle.letterSpacing = '0.06em';
      extraStyle.fontFamily = "'Outfit', sans-serif";
    } else if (variant === 'ghost' || variant === 'link') {
      // no extra
    } else {
      extraStyle.letterSpacing = '0.06em';
      extraStyle.fontFamily = "'Outfit', sans-serif";
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        style={{ ...extraStyle, ...style }}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            LOADING...
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button,buttonVariants };
